import { type ReactElement } from 'react';
import { Brush, Clock, History, User } from 'lucide-react';
import {
  firstNameOf,
  reservationOn,
  type DayMode,
  type MapSpot,
  type SpotStatus,
} from '@kratispot/shared';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export const STATUS_COPY: Readonly<Record<SpotStatus, CopyKey>> = {
  free: 'free',
  reserved: 'reserved',
  occupied: 'occupied',
  cleaning: 'cleaning',
};

// One palette for tiles, legend and panel; the mockup's four status colours.
export const STATUS_TONE: Readonly<Record<SpotStatus, string>> = {
  free: 'border-emerald-300 bg-emerald-50 text-emerald-950',
  reserved: 'border-amber-300 bg-amber-50 text-amber-950',
  occupied: 'border-red-300 bg-red-50 text-red-950',
  cleaning: 'border-sky-300 bg-sky-50 text-sky-950',
};

export const STATUS_BADGE: Readonly<Record<SpotStatus, string>> = {
  free: 'bg-emerald-600',
  reserved: 'bg-amber-500',
  occupied: 'bg-red-500',
  cleaning: 'bg-sky-500',
};

// Free is the quiet state: 150 green ticks say nothing the colour has not. §15
const STATUS_ICON = { free: null, reserved: Clock, occupied: User, cleaning: Brush } as const;

export const PAST_TONE = 'border-stone-300 bg-stone-100 text-stone-500';

// Held, but not settled: a hold's amber with a dashed edge. §13
export const PAYING_TONE = 'border-dashed border-amber-400 bg-amber-50/70 text-amber-900';

// Two states for everyone, and a third only the guest's own tab knows. §11, §12
export const VISITOR_TONE = {
  available: 'border-emerald-400 bg-emerald-50 text-emerald-900 hover:border-emerald-600',
  unavailable: 'border-stone-300 bg-stone-100 text-stone-400 cursor-not-allowed opacity-70',
  yours: 'border-sky-500 bg-sky-100 text-sky-900 cursor-not-allowed font-bold',
} as const;

export function guestLabel(spot: MapSpot, walkIn: string): string {
  const booking = reservationOn(spot);
  if (spot.status === 'reserved' && booking !== null) return firstNameOf(booking.guestName);
  if (spot.status !== 'occupied') return '';
  return booking === null ? walkIn : firstNameOf(booking.guestName);
}

// A tile shows a first name at best, so the whole name rides on the hover. §15
export function guestNoteOf(spot: MapSpot): string | null {
  return reservationOn(spot)?.guestName ?? null;
}

export function SpotTile({
  spot,
  mode,
  compact,
  selected,
  pulsing,
  price,
  mine,
  onPick,
  onHover,
}: {
  spot: MapSpot;
  mode: DayMode;
  compact: boolean;
  selected?: boolean;
  pulsing?: boolean;
  price?: string;
  mine?: boolean;
  onPick: (spot: MapSpot) => void;
  onHover?: (spot: MapSpot, at: { x: number; y: number } | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  if (price !== undefined)
    return (
      <VisitorTile
        spot={spot}
        price={price}
        compact={compact}
        selected={selected}
        mine={mine === true}
        onPick={onPick}
      />
    );
  const status = mode === 'past' ? null : spot.status;
  // A checkout in flight is held but unsettled: the amber of a hold, dashed. §13
  const paying = spot.pendingCheckout && status === 'free';
  const guest = status === null || paying ? '' : guestLabel(spot, t('walkIn'));
  const Icon = status === null ? History : STATUS_ICON[status];
  const badge =
    status === null ? (
      <b className={cn('font-extrabold', compact ? 'text-[7px]' : 'text-[10px]')}>
        {spot.occupations}
      </b>
    ) : Icon === null ? null : (
      <Icon className={compact ? 'size-1.5' : 'size-2.5'} strokeWidth={3.4} />
    );
  const label = paying
    ? `${spot.number} · ${t('payingNow')}`
    : status === null
      ? `${spot.number} · ${spot.occupations} ${t('occupations')}`
      : `${spot.number} · ${t(STATUS_COPY[status])}`;

  return (
    <button
      type="button"
      data-testid={`spot-${spot.number}`}
      data-spot-id={spot.id}
      data-status={status ?? 'past'}
      data-pending={paying ? true : undefined}
      aria-pressed={selected === true ? true : undefined}
      aria-label={label}
      onClick={() => onPick(spot)}
      onPointerEnter={(event) =>
        event.pointerType === 'mouse' && onHover?.(spot, { x: event.clientX, y: event.clientY })
      }
      onPointerLeave={() => onHover?.(spot, null)}
      className={cn(
        'relative flex h-full w-full flex-col items-center justify-center gap-0.5 border-2 text-center transition-transform hover:-translate-y-0.5 hover:shadow-lg',
        compact ? 'rounded-md' : 'rounded-2xl',
        status === null ? PAST_TONE : STATUS_TONE[status],
        paying && PAYING_TONE,
        selected === true && 'spot-picked',
        pulsing === true && 'spot-pulse',
      )}
    >
      {badge !== null && (
        <span
          aria-hidden
          className={cn(
            'absolute -top-1.5 -end-1.5 grid place-items-center rounded-full border-2 border-card text-white',
            compact ? 'size-3' : 'size-[18px]',
            status === null ? 'bg-violet-600' : STATUS_BADGE[status],
          )}
        >
          {badge}
        </span>
      )}
      <b className={cn('leading-none font-extrabold', compact ? 'text-[11px]' : 'text-[15px]')}>
        {spot.number}
      </b>
      {guest !== '' && !compact && (
        <span className="max-w-full truncate px-1 text-[9px] font-semibold">{guest}</span>
      )}
      {spot.staffOnly && (
        <span
          title={t('staffOnly')}
          className={cn(
            'absolute grid place-items-center rounded-full border border-amber-300 bg-white font-extrabold text-amber-600',
            compact
              ? '-bottom-1 -end-1 size-2.5 text-[7px] leading-none'
              : '-bottom-1 -end-1 size-[15px] text-[9px]',
          )}
        >
          ★
        </span>
      )}
    </button>
  );
}

// The visitor's own tile: its number, its price, and which of two states it is.
function VisitorTile({
  spot,
  price,
  compact,
  selected,
  mine,
  onPick,
}: {
  spot: MapSpot;
  price: string;
  compact: boolean;
  selected?: boolean;
  mine: boolean;
  onPick: (spot: MapSpot) => void;
}): ReactElement {
  const { t } = useTranslation();
  const available = spot.status === 'free';
  // Free is nobody's, whatever this tab remembers: a booking may have gone. §12
  const theirs = mine && !available;
  const state = theirs ? 'yours' : available ? 'available' : 'unavailable';
  const word = t(theirs ? 'yourSpot' : available ? 'available' : 'unavailable');

  return (
    <button
      type="button"
      data-testid={`spot-${spot.number}`}
      data-spot-id={spot.id}
      data-available={available}
      data-mine={theirs ? true : undefined}
      disabled={!available}
      aria-pressed={available ? selected === true : undefined}
      aria-label={`${spot.number} · ${word}`}
      onClick={() => onPick(spot)}
      className={cn(
        'flex h-full w-full flex-col items-center justify-center gap-0.5 overflow-hidden border-2 text-center transition-transform',
        compact ? 'rounded-md' : 'rounded-2xl',
        available && 'hover:-translate-y-0.5 hover:shadow-lg',
        VISITOR_TONE[state],
        selected === true && 'spot-picked',
      )}
    >
      <b className={cn('leading-none font-extrabold', compact ? 'text-[11px]' : 'text-[15px]')}>
        {spot.number}
      </b>
      {available && !compact && (
        <span className="max-w-full truncate px-1 text-[9px] font-bold">{price}</span>
      )}
    </button>
  );
}
